import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { ProductCard } from "@/components/shop/ProductCard";
import { fetchProducts, type ShopifyProduct } from "@/lib/shopify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

const searchSchema = z.object({
  q: z.string().optional(),
  cat: z.string().optional(),
  sort: z.enum(["featured", "price-asc", "price-desc"]).optional(),
});

export const Route = createFileRoute("/shop")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ShopPage,
});

const categories = [
  { id: "", label: "All Products" },
  { id: "home", label: "Home Gadgets" },
  { id: "kitchen", label: "Kitchen Essentials" },
  { id: "watch", label: "Smart Watches" },
  { id: "toy", label: "Kids Toys" },
];

function ShopPage() {
  const { q, cat, sort } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(q ?? "");

  useEffect(() => {
    setLoading(true);
    fetchProducts(undefined, 100)
      .then(setProducts)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = [...products];
    const term = (q ?? "").toLowerCase().trim();
    if (term) {
      list = list.filter(
        (p) =>
          p.node.title.toLowerCase().includes(term) ||
          p.node.description?.toLowerCase().includes(term) ||
          p.node.productType?.toLowerCase().includes(term) ||
          p.node.tags?.some((t) => t.toLowerCase().includes(term)),
      );
    }
    if (cat) {
      list = list.filter(
        (p) =>
          p.node.title.toLowerCase().includes(cat) ||
          p.node.productType?.toLowerCase().includes(cat) ||
          p.node.tags?.some((t) => t.toLowerCase().includes(cat)),
      );
    }
    if (sort === "price-asc") {
      list.sort((a, b) => parseFloat(a.node.priceRange.minVariantPrice.amount) - parseFloat(b.node.priceRange.minVariantPrice.amount));
    } else if (sort === "price-desc") {
      list.sort((a, b) => parseFloat(b.node.priceRange.minVariantPrice.amount) - parseFloat(a.node.priceRange.minVariantPrice.amount));
    }
    return list;
  }, [products, q, cat, sort]);

  return (
    <>
      <Helmet>
        <title>Shop</title>
        <meta name="description" content="Browse trending gadgets, smart watches, kitchen tools and toys. Cash on Delivery available." />
        <meta property="og:title" content="Shop — Trendora Marts" />
      </Helmet>
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <p className="text-gold uppercase text-xs tracking-[0.3em] mb-3">Our Collection</p>
          <h1 className="text-4xl sm:text-5xl font-serif">Shop All Products</h1>
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          {/* SIDEBAR */}
          <aside className="space-y-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                navigate({ search: (s: z.infer<typeof searchSchema>) => ({ ...s, q: search || undefined }) });
              }}
              className="flex gap-2"
            >
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." maxLength={80} />
              <Button type="submit" size="icon" variant="dark"><Search className="h-4 w-4" /></Button>
            </form>

            <div>
              <h3 className="font-serif text-lg mb-3">Categories</h3>
              <ul className="space-y-1">
                {categories.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => navigate({ search: (s: z.infer<typeof searchSchema>) => ({ ...s, cat: c.id || undefined }) })}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        (cat ?? "") === c.id ? "bg-gradient-gold text-gold-foreground" : "hover:bg-card"
                      }`}
                    >
                      {c.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-serif text-lg mb-3">Sort By</h3>
              <select
                value={sort ?? "featured"}
                onChange={(e) => navigate({ search: (s: z.infer<typeof searchSchema>) => ({ ...s, sort: e.target.value as "featured" | "price-asc" | "price-desc" }) })}
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="featured">Featured</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
              </select>
            </div>
          </aside>

          {/* PRODUCTS */}
          <div>
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-square bg-muted animate-pulse rounded-lg" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-border rounded-lg">
                <p className="text-lg font-serif mb-2">No products found</p>
                <p className="text-sm text-muted-foreground">Try a different search or check back soon.</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-6">{filtered.length} product{filtered.length !== 1 ? "s" : ""}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  {filtered.map((p) => <ProductCard key={p.node.id} product={p} />)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
